import TagManager from 'react-gtm-module';

export interface GTMEvent {
  event: string;
  [key: string]: string | number | boolean | undefined;
}

export interface GTMConfig {
  gtmId: string;
  dataLayer?: Record<string, unknown>;
  dataLayerName?: string;
  auth?: string;
  preview?: string;
}

/**
 * Initializes Google Tag Manager
 * @param config - GTM configuration
 * @throws Error if GTM ID is missing or invalid
 */
export const initializeGTM = (config: GTMConfig): void => {
  if (!config.gtmId || !config.gtmId.startsWith('GTM-')) {
    if (import.meta.env.DEV) {
      console.warn('GTM: Invalid or missing GTM container ID, tracking disabled');
    }
    return;
  }

  try {
    TagManager.initialize({
      gtmId: config.gtmId,
      dataLayer: config.dataLayer,
      dataLayerName: config.dataLayerName || 'dataLayer',
    });
    if (import.meta.env.DEV) {
      
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('GTM initialization failed:', error);
    }
  }
};

/**
 * Tracks a custom event in GTM
 * @param eventData - Event name and properties
 */
export const trackEvent = (eventData: GTMEvent): void => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    if (import.meta.env.DEV) {
      console.warn('GTM: dataLayer not available, event not tracked:', eventData.event);
    }
    return;
  }

  // Sanitize: Remove any potential PII fields
  const sanitized = sanitizeEventData(eventData);
  TagManager.dataLayer({ dataLayer: sanitized });
};

/**
 * Tracks a page view
 * @param path - Page path
 * @param title - Page title (optional)
 */
export const trackPageView = (path: string, title?: string): void => {
  trackEvent({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
  });
};

/**
 * Sanitizes event data to prevent PII leakage
 * @param data - Raw event data
 * @returns Sanitized event data
 */
const sanitizeEventData = (data: GTMEvent): GTMEvent => {
  const sensitiveKeys = ['email', 'password', 'firstName', 'lastName', 'confirmPassword', 'first_name', 'last_name'];
  const sanitized: GTMEvent = { ...data };

  sensitiveKeys.forEach((key) => {
    if (key in sanitized) {
      delete sanitized[key];
    }
  });

  return sanitized;
};
